import { classSet } from '@platform/ui-library';
import { FC, useEffect } from 'react';
import { KnownClientId, RootRoute } from '@app/const';
import {
  useAsync,
  useDesign,
  useRootRoute,
  useServices
} from '@app/hooks';
import styles from './AppNav.module.scss';

type NavButtonProps = {
  activeRoute: RootRoute;
  route: RootRoute;
  setRoute: (newRoute: RootRoute) => void;
};

/** Button in the top nav menu. */
const NavButton: FC<NavButtonProps> = ({
  activeRoute,
  route,
  setRoute
}) => {
  return (
    <a
      className={classSet([
        styles.navButton,
        'nav-button',
        (activeRoute !== route) ? null : 'selected'
      ])}
      href={`#${route}`}
      // `href` is kept for middle-click & copy-link, but its default hash change
      // would navigate a second time on top of `setRoute`.
      onClick={(event) => {
        event.preventDefault();
        setRoute(route);
      }}
    >{window.l.header[route]}</a>
  );
};

export const AppNav: FC = () => {
  const { avsService } = useServices();
  const [activeRoute, setRoute] = useRootRoute();
  const serialized = useDesign();
  const [designInfo, designInfoLoading] = useAsync(() => {
    return avsService.getDesignInfo(serialized.designId);
  }, [serialized.designId]);
  const isCommonModule = serialized.clientId === KnownClientId.interactionsCommon;
  const hasAgenticApps = !!designInfo?.agenticApplications?.length;

  useEffect(() => {
    if (
      activeRoute === RootRoute.agentic
      && !designInfoLoading
      && !hasAgenticApps
    ) {
      setRoute(RootRoute.canvas);
    }
  }, [activeRoute, designInfoLoading, hasAgenticApps]);

  const navConfig: Omit<NavButtonProps, 'route'> = {
    activeRoute,
    setRoute
  };

  return (
    <div className={styles.navContainer}>
      <NavButton route={RootRoute.canvas} {...navConfig} />
      {(!hasAgenticApps) ? null : (
        <NavButton route={RootRoute.agentic} {...navConfig} />
      )}
      {(isCommonModule) ? null : (
        <NavButton route={RootRoute.intents} {...navConfig} />
      )}
      {(isCommonModule) ? null : (
        <NavButton route={RootRoute.testTool} {...navConfig} />
      )}
      <NavButton route={RootRoute.settings} {...navConfig} />
      <NavButton route={RootRoute.save} {...navConfig} />
    </div>
  );
};
